import { useState, useCallback, useRef, useEffect } from "react";
import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import * as Popover from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  Cross2Icon,
  DotsVerticalIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { toast } from "sonner";

import styles from "./Agents.module.scss";
import { DeleteTeamDialog } from "./DeleteTeamDialog";
import {
  useTeamsWithAgents,
  useAgents,
  useDeleteTeam,
  useRemoveAgentFromTeam,
  useAddAgentToTeam,
} from "../../api/useTeams";

interface Agent {
  id: string;
  name: string;
  externalId: string;
  teams?: string[];
}

interface Team {
  id: string;
  name: string;
}

interface TeamDetailsProps {
  team: Team | null;
  onClose: () => void;
}

export function TeamDetails({ team, onClose }: TeamDetailsProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddAgentOpen, setIsAddAgentOpen] = useState(false);
  const [addAgentSearchQuery, setAddAgentSearchQuery] = useState("");
  const [currentAgentId, setCurrentAgentId] = useState("");
  const [isAddingAgent, setIsAddingAgent] = useState(false);

  const {
    data: teamsData,
    isLoading: isTeamsLoading,
    refetch: refetchTeams,
  } = useTeamsWithAgents(true);
  const {
    data: agentsData = [],
    isLoading: isAgentsLoading,
    refetch: refetchAgents,
  } = useAgents(true);

  const { mutateAsync: deleteTeam } = useDeleteTeam(team?.id || "");
  const { mutateAsync: removeAgent } = useRemoveAgentFromTeam(
    "",
    team?.id || ""
  );
  const { mutateAsync: addAgent } = useAddAgentToTeam(
    currentAgentId,
    team?.id || ""
  );

  useEffect(() => {
    async function addAgentToTeam() {
      if (!isAddingAgent || !currentAgentId || !team) return;

      try {
        await addAgent({
          teamIds: [team.id],
        });
        await Promise.all([refetchTeams(), refetchAgents()]);
        setIsAddAgentOpen(false);
        setAddAgentSearchQuery("");
        toast("Agent added to team successfully");
      } catch (error) {
        try {
          // window.quack?.proactiveTrigger();
        } catch (e) {}
        console.error("Failed to add agent to team:", error);
        toast("Failed to add agent to team");
      } finally {
        setIsAddingAgent(false);
      }
    }

    addAgentToTeam();
  }, [
    currentAgentId,
    isAddingAgent,
    team,
    addAgent,
    refetchTeams,
    refetchAgents,
  ]);

  const handleRemoveAgent = useCallback(
    async (agent: Agent) => {
      if (!team) return;

      try {
        await removeAgent({});
        toast(`Removed ${agent.name} from the team`);
        await Promise.all([refetchTeams(), refetchAgents()]);
      } catch (error) {
        try {
          // window.quack?.proactiveTrigger();
        } catch (e) {}
        console.error("Failed to remove agent from team:", error);
        toast(`Failed to remove ${agent.name} from the team`);
      }
    },
    [team, removeAgent, refetchTeams, refetchAgents]
  );

  const handleAddAgent = useCallback(
    (agentId: string) => {
      if (!team) return;
      setCurrentAgentId(agentId);
      setIsAddingAgent(true);
    },
    [team]
  );

  const handleDeleteTeam = useCallback(async () => {
    if (!team) return;

    try {
      await deleteTeam({});
      toast("Team deleted successfully");
      onClose();
    } catch (error) {
      try {
        // window.quack?.proactiveTrigger();
      } catch (e) {}
      console.error("Failed to delete team:", error);
      toast("Failed to delete team");
    }
  }, [team, deleteTeam, onClose]);

  if (!team || !teamsData) return null;

  const teamData = teamsData[team.name];
  const teamAgents = agentsData.filter((agent) => {
    if (!teamData?.agents) return false;
    const teamAgentIds = new Set(teamData.agents.map((a) => a.id));
    return teamAgentIds.has(agent.id) || teamAgentIds.has(agent.externalId);
  });

  const availableAgents = agentsData.filter((agent) => {
    if (!teamData?.agents) return true;
    const teamAgentIds = new Set(teamData.agents.map((a) => a.id));
    return (
      !teamAgentIds.has(agent.id) &&
      !teamAgentIds.has(agent.externalId) &&
      agent.name.toLowerCase().includes(addAgentSearchQuery.toLowerCase())
    );
  });

  // Handle loading state
  if (isTeamsLoading || isAgentsLoading) {
    return (
      <>
        <div className={styles.modalOverlay} onClick={onClose} />
        <div className={styles.rightModal}>
          <div className={styles.modalHeader}>
            <h2>Loading...</h2>
          </div>
          <div className={styles.modalContent}>
            <Flex justify="center" py="8">
              <Text size="2" color="gray">
                Loading team details...
              </Text>
            </Flex>
          </div>
        </div>
      </>
    );
  }

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div className={styles.rightModal}>
        <div className={styles.modalHeader}>
          <Flex gap="3" direction="column" width="100%">
            <Flex justify="between" align="center" width="100%">
              <Button
                variant="ghost"
                color="gray"
                size="2"
                className={styles.squareIconButton}
                onClick={onClose}
              >
                <TooltipPrimitive.Root>
                  <TooltipPrimitive.Trigger asChild>
                    <Cross2Icon />
                  </TooltipPrimitive.Trigger>
                  <TooltipPrimitive.Content
                    className={styles.tooltipContent}
                    sideOffset={5}
                  >
                    Close
                    <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Root>
              </Button>
            </Flex>
            <Flex justify="between" align="center" width="100%">
              <Box>
                <h2>{team.name}</h2>
              </Box>
              <Button
                variant="surface"
                color="red"
                onClick={() => setIsDeleteDialogOpen(true)}
                className={styles.deleteButton}
              >
                <TooltipPrimitive.Root>
                  <TooltipPrimitive.Trigger asChild>
                    <Flex gap="2" align="center">
                      <TrashIcon />
                      Delete Team
                    </Flex>
                  </TooltipPrimitive.Trigger>
                  <TooltipPrimitive.Content
                    className={styles.tooltipContent}
                    sideOffset={5}
                  >
                    Delete this team
                    <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Root>
              </Button>
            </Flex>
          </Flex>
        </div>

        <div className={styles.modalContent}>
          <Flex direction="column" gap="3">
            <Popover.Root
              open={isAddAgentOpen}
              onOpenChange={setIsAddAgentOpen}
            >
              <Popover.Trigger asChild>
                <Button
                  variant="outline"
                  color="gray"
                  className={`${styles.actionButton} ${styles.addButton}`}
                >
                  <PlusIcon />
                  Add Agent
                </Button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className={styles.popoverContent}
                  side="bottom"
                  align="start"
                  sideOffset={5}
                  alignOffset={0}
                >
                  <input
                    type="text"
                    className={styles.commandInput}
                    placeholder="Search agents..."
                    value={addAgentSearchQuery}
                    onChange={(e) => setAddAgentSearchQuery(e.target.value)}
                    autoFocus={false}
                  />
                  <div className={styles.commandList}>
                    {isAgentsLoading ? (
                      <div className={styles.commandEmpty}>
                        Loading agents...
                      </div>
                    ) : availableAgents.length === 0 ? (
                      <div className={styles.commandEmpty}>
                        {addAgentSearchQuery
                          ? "No matching agents found"
                          : "No available agents"}
                      </div>
                    ) : (
                      <div className={styles.commandGroup}>
                        {availableAgents.map((agent) => (
                          <button
                            key={agent.id}
                            className={styles.commandItem}
                            onClick={() => handleAddAgent(agent.externalId)}
                          >
                            <span>{agent.name}</span>
                            <PlusIcon className={styles.addIcon} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>

            <Text size="2" color="gray">
              {teamAgents.length} {teamAgents.length === 1 ? "agent" : "agents"}{" "}
              in team
            </Text>

            {teamAgents.length === 0 ? (
              <Flex direction="column" align="center" gap="3" py="6">
                <Text size="2" color="gray">
                  No agents in this team yet
                </Text>
                <Text size="2" color="gray">
                  Add agents using the button above
                </Text>
              </Flex>
            ) : (
              teamAgents.map((agent) => (
                <div key={agent.id} className={styles.listItem}>
                  <div className={styles.itemContent}>
                    <span className={styles.itemName}>{agent.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="1"
                    className={styles.removeButton}
                    onClick={() => handleRemoveAgent(agent)}
                  >
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <Cross2Icon />
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Content
                        className={styles.tooltipContent}
                        sideOffset={5}
                      >
                        Remove from team
                        <TooltipPrimitive.Arrow
                          className={styles.tooltipArrow}
                        />
                      </TooltipPrimitive.Content>
                    </TooltipPrimitive.Root>
                  </Button>
                </div>
              ))
            )}
          </Flex>
        </div>

        <DeleteTeamDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          teamName={teamData?.name || ""}
          hasAgents={teamData?.agents?.length > 0}
          onConfirmDelete={handleDeleteTeam}
        />
      </div>
    </TooltipPrimitive.Provider>
  );
}
